/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectDestination.tsx
 🗓️ Created: 2025-01-14
 🧩 Description:
    Reusable component for selecting a destination based on the
    selected module. Dynamically loads destinations based on module type.

 🔧 Notes:
    - Searchable select component with tooltips
    - Linked to SelectModule component
    - Currently supports "Terminate Call" module with SIP cause codes
    - Will be expanded to support more modules (Extensions, IVRs, etc.)

 🔁 Last Updated: 2025-01-14 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, Info, Loader2 } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useDestinationOptions, type DestinationOption } from '../../hooks/useDestinationOptions';
import SimpleTooltip from './SimpleTooltip';

interface SelectDestinationProps {
  moduleType: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  id?: string;
}

// Terminate Call Cause Codes (hardcoded as requested)
const TERMINATE_CAUSE_KEYS = [
  "NORMAL_CLEARING",
  "USER_BUSY",
  "NETWORK_CONGESTION",
  "NO_ROUTE_DESTINATION",
  "UNALLOCATED_NUMBER",
  "CALL_REJECTED",
  "SERVICE_UNAVAILABLE",
  "ALL_CIRCUITS_BUSY",
  "NORMAL_UNSPECIFIED"
];

export default function SelectDestination({
  moduleType,
  value,
  onChange,
  placeholder = 'Select Destination',
  disabled = false,
  className = '',
  error = false,
  id
}: SelectDestinationProps) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  // Fetch destinations dynamically from API (except terminate_call which is hardcoded)
  const { destinations: apiDestinations, isLoading: isLoadingDestinations } = useDestinationOptions(moduleType);

  // Get available destinations for the selected module
  const availableDestinations = useMemo(() => {
    if (moduleType === 'terminate_call') {
      // Hardcoded terminate call options
      return TERMINATE_CAUSE_KEYS.map(key => ({
        value: key,
        label: t(`outboundRoutes.terminateCauses.${key}.label`),
        tooltip: t(`outboundRoutes.terminateCauses.${key}.tooltip`)
      })) as Array<DestinationOption | { value: string; label: string; tooltip: string }>;
    }
    // Dynamic destinations from API
    return apiDestinations;
  }, [moduleType, apiDestinations, t]);

  // Get selected destination
  const selectedDestination = availableDestinations.find(d => d.value === value);

  // Filter destinations based on search
  const filteredDestinations = availableDestinations.filter(dest =>
    dest.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (dest.tooltip && dest.tooltip.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownContentRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (buttonRef.current && dropdownContentRef.current) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const dropdownRect = dropdownContentRef.current.getBoundingClientRect();
          const dropdownHeight = dropdownRect.height || 240; // Fallback to approximate height
          const spaceBelow = window.innerHeight - buttonRect.bottom;
          const spaceAbove = buttonRect.top;

          // Open upward if there's not enough space below but enough space above
          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setOpenUpward(true);
          } else {
            setOpenUpward(false);
          }
        }
      });
    }
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset value when module changes and current value is invalid
  // Only reset if destinations have finished loading and value is not found
  useEffect(() => {
    if (value && !isLoadingDestinations && availableDestinations.length > 0 && !availableDestinations.find(d => d.value === value)) {
      // Only clear if module changed, not if destinations are still loading
      onChange('');
    }
  }, [moduleType, value, availableDestinations, onChange, isLoadingDestinations]);

  const handleSelect = (destValue: string) => {
    onChange(destValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  const isDisabled = disabled || !moduleType || (availableDestinations.length === 0 && !isLoadingDestinations);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        id={id}
        ref={buttonRef}
        type="button"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md
          bg-background text-foreground
          border-input
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
          transition-colors
        `}
      >
        <span className={selectedDestination ? '' : value ? '' : 'text-muted-foreground'}>
          {selectedDestination 
            ? selectedDestination.label 
            : value 
              ? value 
              : placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          ref={dropdownContentRef}
          className={`absolute z-50 w-full bg-popover border border-border rounded-md shadow-lg ${
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search destinations..."
              className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {isLoadingDestinations ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading destinations...</span>
              </div>
            ) : filteredDestinations.length > 0 ? (
              filteredDestinations.map((dest) => (
                <button
                  key={dest.value}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(dest.value);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  className={`
                    w-full px-3 py-2 text-left rounded-md cursor-pointer
                    transition-colors flex items-center justify-between gap-2
                    ${value === dest.value 
                      ? 'bg-primary/20 text-primary font-semibold' 
                      : 'hover:bg-primary/15 text-foreground'}
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium">{dest.label}</div>
                    {('description' in dest && dest.description) && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {(dest as DestinationOption).description}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {dest.tooltip && (
                      <SimpleTooltip content={dest.tooltip}>
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      </SimpleTooltip>
                    )}
                    {value === dest.value && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {moduleType ? 'No destinations found' : 'Select a module first'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

